import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import { Close as CloseIcon } from '@mui/icons-material';
import { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import {
  TColumnPermissions,
  TFilterData,
  TState as TMainState,
  TSelectOption,
} from '..';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import { getCurriculumV2State } from '../../../../../../store/V2/CurriculumSlice';
import { useAppSelector } from '../../../../../../hooks';
import {
  TMeetingLessonTypeOption,
  TMeetingSectionPaymentTypeOption,
  meetingLessonTypeOptions,
  meetingSectionPaymentTypeOptions,
} from '../../../../../../constants';
import { capitalizeFirstLetter } from '../../../../../../helper';

interface FilterListModalProps {
  open: boolean;
  onClose: () => void;
  filters: TFilterData;
  mainState: TMainState;
  columnPermissions: TColumnPermissions;
  coursesOptions: TSelectOption[];
  courseStudentOptions: TSelectOption[];
  courseTutorOptions: TSelectOption[];
  onFiltersApplied: (
    filterData: TFilterData,
    stateAttributes: Partial<TMainState>
  ) => void;
}

type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
  selectedCourse: TSelectOption | null;
  selectedCourseStudent: TSelectOption | null;
  selectedCourseTutor: TSelectOption | null;
  referenceKey: string;
  selectedCurriculum: TSelectOption | null;
  selectedGrade: TSelectOption | null;
  selectedSubject: TSelectOption | null;
  selectedLessonType: TMeetingLessonTypeOption | null;
  selectedPaymentType: TMeetingSectionPaymentTypeOption | null;
};

const initialState: TState = {
  startDate: null,
  endDate: null,
  selectedCourse: null,
  selectedCourseStudent: null,
  selectedCourseTutor: null,
  referenceKey: '',
  selectedCurriculum: null,
  selectedGrade: null,
  selectedSubject: null,
  selectedLessonType: null,
  selectedPaymentType: null,
};

const FilterListModal: FunctionComponent<FilterListModalProps> = ({
  open,
  onClose,
  filters,
  mainState,
  columnPermissions,
  coursesOptions,
  courseStudentOptions,
  courseTutorOptions,
  onFiltersApplied,
}) => {
  // Store
  const curriculumState = useAppSelector(getCurriculumV2State);

  // State
  const [state, setState] = useState({ ...initialState });
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);

  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );

  useEffect(() => {
    setState((prevState) => {
      const newState = { ...prevState };

      if (mainState.startDate) {
        newState.startDate = mainState.startDate.clone();
      }

      if (mainState.endDate) {
        newState.endDate = mainState.endDate.clone();
      }

      if (mainState.selectedCourse) {
        newState.selectedCourse = mainState.selectedCourse;
      }

      if (mainState.selectedCourseStudent) {
        newState.selectedCourseStudent = mainState.selectedCourseStudent;
      }

      if (mainState.selectedCourseTutor) {
        newState.selectedCourseTutor = mainState.selectedCourseTutor;
      }

      if (filters.referenceKey) {
        newState.referenceKey = filters.referenceKey;
      }

      if (mainState.selectedCurriculum) {
        newState.selectedCurriculum = mainState.selectedCurriculum;
      }

      if (mainState.selectedSubject) {
        newState.selectedSubject = mainState.selectedSubject;
      }

      if (mainState.selectedGrade) {
        newState.selectedGrade = mainState.selectedGrade;
      }

      if (mainState.selectedLessonType) {
        newState.selectedLessonType = mainState.selectedLessonType;
      }

      if (mainState.selectedPaymentType) {
        newState.selectedPaymentType = mainState.selectedPaymentType;
      }

      return newState;
    });

    if (curriculumState.list && curriculumState.list.length) {
      setCurriculumOptions(
        curriculumState.list.map((curriculumItem) => ({
          label: curriculumItem.curriculum,
          value: curriculumItem.curriculum,
        }))
      );

      if (mainState.selectedCurriculum) {
        const matchingCurriculum = curriculumState.list.find(
          (curriculumItem) =>
            curriculumItem.curriculum === mainState.selectedCurriculum?.value
        );

        if (
          matchingCurriculum &&
          matchingCurriculum.subjects &&
          matchingCurriculum.subjects.length
        ) {
          setSubjectOptions(
            matchingCurriculum.subjects.map((subjectItem) => ({
              label: subjectItem.subject,
              value: subjectItem.subject,
            }))
          );

          if (mainState.selectedSubject) {
            const matchingSubject = matchingCurriculum.subjects.find(
              (subjectItem) =>
                subjectItem.subject === mainState.selectedSubject?.value
            );

            if (
              matchingSubject &&
              matchingSubject.grade &&
              matchingSubject.grade.length
            ) {
              setGradeOptions(
                matchingSubject.grade.map((gradeItem) => ({
                  label: gradeItem.grade,
                  value: gradeItem.grade,
                }))
              );
            }
          }
        }
      }
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newSubjectOptions: TSelectOption[] = [];
    const matchingCurriculum = curriculumState.list.find(
      (curriculumItem) => curriculumItem.curriculum === value?.value
    );

    if (
      matchingCurriculum &&
      matchingCurriculum.subjects &&
      matchingCurriculum.subjects.length
    ) {
      matchingCurriculum.subjects.forEach((subjectItem) => {
        newSubjectOptions.push({
          label: subjectItem.subject,
          value: subjectItem.subject,
        });
      });
    }

    updateStateAttributes({
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    });

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const selectedCurriculumData = curriculumState.list.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        const selectedSubject = selectedCurriculumData.subjects.find(
          (subjectItem) => subjectItem.subject === value?.value
        );

        if (
          selectedSubject &&
          selectedSubject.grade &&
          selectedSubject.grade.length
        ) {
          selectedSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    updateStateAttributes({
      selectedSubject: value,
      selectedGrade: null,
    });

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedGrade: value,
    });
  };

  const handleSubmit = () => {
    const newFilters: TFilterData = {
      ...filters,
      startDate: undefined,
      endDate: undefined,
      courseId: undefined,
      studentId: undefined,
      tutorId: undefined,
      referenceKey: undefined,
      curriculum: undefined,
      grade: undefined,
      subject: undefined,
      lessonType: undefined,
      paymentType: undefined,
    };

    if (state.startDate) {
      newFilters.startDate = state.startDate
        .clone()
        .startOf('day')
        .toISOString();
    }

    if (state.endDate) {
      newFilters.endDate = state.endDate.clone().endOf('day').toISOString();
    }

    if (state.selectedCourse) {
      newFilters.courseId = state.selectedCourse.value;
    }

    if (state.selectedCourseStudent) {
      newFilters.studentId = state.selectedCourseStudent.value;
    }

    if (state.selectedCourseTutor) {
      newFilters.tutorId = state.selectedCourseTutor.value;
    }

    if (state.referenceKey.trim().length) {
      newFilters.referenceKey = state.referenceKey.trim();
    }

    if (state.selectedCurriculum) {
      newFilters.curriculum = state.selectedCurriculum.value;
    }

    if (state.selectedGrade) {
      newFilters.grade = state.selectedGrade.value;
    }

    if (state.selectedSubject) {
      newFilters.subject = state.selectedSubject.value;
    }

    if (state.selectedLessonType) {
      newFilters.lessonType = state.selectedLessonType.value;
    }

    if (state.selectedPaymentType) {
      newFilters.paymentType = state.selectedPaymentType.value;
    }

    onFiltersApplied(newFilters, {
      startDate: state.startDate,
      endDate: state.endDate,
      selectedCourse: state.selectedCourse,
      selectedCourseStudent: state.selectedCourseStudent,
      selectedCourseTutor: state.selectedCourseTutor,
      referenceKey: state.referenceKey,
      selectedCurriculum: state.selectedCurriculum,
      selectedGrade: state.selectedGrade,
      selectedSubject: state.selectedSubject,
      selectedLessonType: state.selectedLessonType,
      selectedPaymentType: state.selectedPaymentType,
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', maxWidth: '850px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Filters
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={2}>
              {columnPermissions.dateTime ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <DatePicker
                        clearable
                        value={state.startDate}
                        label="Start Date"
                        inputFormat="YYYY-MM-DD"
                        onChange={(date) =>
                          updateStateAttributes({
                            startDate: date,
                          })
                        }
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...defaultTextFieldProps}
                            size="small"
                            placeholder="Select"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <DatePicker
                        clearable
                        value={state.endDate}
                        label="End Date"
                        inputFormat="YYYY-MM-DD"
                        onChange={(date) =>
                          updateStateAttributes({
                            endDate: date,
                          })
                        }
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...defaultTextFieldProps}
                            size="small"
                            placeholder="Select"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={state.selectedCourse}
                    options={coursesOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    onChange={(e, value) =>
                      updateStateAttributes({ selectedCourse: value })
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        label="Course"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              {courseStudentOptions.length && columnPermissions.students ? (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={state.selectedCourseStudent}
                      options={courseStudentOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      onChange={(e, value) =>
                        updateStateAttributes({ selectedCourseStudent: value })
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...defaultTextFieldProps}
                          size="small"
                          label="Student"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              ) : null}
              {courseTutorOptions.length && columnPermissions.tutors ? (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      size="small"
                      value={state.selectedCourseTutor}
                      options={courseTutorOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      onChange={(e, value) =>
                        updateStateAttributes({ selectedCourseTutor: value })
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...defaultTextFieldProps}
                          size="small"
                          label="Tutor"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              ) : null}
              {columnPermissions.referenceKey ? (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <TPTextField
                      {...defaultTextFieldProps}
                      size="small"
                      label="Reference Key"
                      placeholder="Enter reference key"
                      value={state.referenceKey}
                      onChange={(e) =>
                        updateStateAttributes({ referenceKey: e.target.value })
                      }
                    />
                  </FormControl>
                </Grid>
              ) : null}
              {columnPermissions.curriculum ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <Autocomplete
                        size="small"
                        value={state.selectedCurriculum}
                        options={curriculumOptions}
                        popupIcon={TPAutoCompleteArrowIconComponent}
                        getOptionKey={(option: TSelectOption) => option.value}
                        isOptionEqualToValue={(option, value) =>
                          option.label === value.value
                        }
                        onChange={handleCurriculumChange}
                        renderInput={(params) => (
                          <TPTextField
                            {...params}
                            {...defaultTextFieldProps}
                            size="small"
                            label="Curriculum"
                            placeholder="Select"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {columnPermissions.subject ? (
                    <>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                          <Autocomplete
                            size="small"
                            disabled={!state.selectedCurriculum}
                            value={state.selectedSubject}
                            options={subjectOptions}
                            popupIcon={TPAutoCompleteArrowIconComponent}
                            getOptionKey={(option: TSelectOption) =>
                              option.value
                            }
                            isOptionEqualToValue={(option, value) =>
                              option.label === value.value
                            }
                            onChange={handleSubjectChange}
                            renderInput={(params) => (
                              <TPTextField
                                {...params}
                                {...defaultTextFieldProps}
                                size="small"
                                label="Subject"
                                placeholder="Select"
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      {columnPermissions.grade ? (
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth>
                            <Autocomplete
                              size="small"
                              disabled={!state.selectedSubject}
                              value={state.selectedGrade}
                              options={gradeOptions}
                              popupIcon={TPAutoCompleteArrowIconComponent}
                              getOptionKey={(option: TSelectOption) =>
                                option.value
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.label === value.value
                              }
                              onChange={handleGradeChange}
                              renderInput={(params) => (
                                <TPTextField
                                  {...params}
                                  {...defaultTextFieldProps}
                                  size="small"
                                  label="Grade"
                                  placeholder="Select"
                                />
                              )}
                            />
                          </FormControl>
                        </Grid>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : null}
              {columnPermissions.lessonType ? (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      size="small"
                      value={state.selectedLessonType}
                      options={meetingLessonTypeOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      getOptionKey={(option) => option.value}
                      isOptionEqualToValue={(option, value) =>
                        option.label === value.value
                      }
                      onChange={(event, value) =>
                        updateStateAttributes({ selectedLessonType: value })
                      }
                      getOptionLabel={(option) =>
                        capitalizeFirstLetter(option.label.toLowerCase())
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...defaultTextFieldProps}
                          size="small"
                          label="Lesson Type"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              ) : null}
              {columnPermissions.paymentType ? (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      size="small"
                      value={state.selectedPaymentType}
                      options={meetingSectionPaymentTypeOptions}
                      popupIcon={TPAutoCompleteArrowIconComponent}
                      getOptionKey={(option) => option.value}
                      isOptionEqualToValue={(option, value) =>
                        option.label === value.value
                      }
                      onChange={(event, value) =>
                        updateStateAttributes({ selectedPaymentType: value })
                      }
                      getOptionLabel={(option) =>
                        capitalizeFirstLetter(
                          option.label.toLowerCase().replace('_', ' ')
                        )
                      }
                      renderInput={(params) => (
                        <TPTextField
                          {...params}
                          {...defaultTextFieldProps}
                          size="small"
                          label="Payment Type"
                          placeholder="Select"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              ) : null}
            </Grid>
          </LocalizationProvider>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkButton
          disableElevation
          variant="contained"
          color="inherit"
          sx={{ ...defaultButtonStyles }}
          onClick={handleSubmit}
        >
          Apply Filters
        </DarkButton>
      </DialogActions>
    </Dialog>
  );
};

export default FilterListModal;
